import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { JamesProvider, useJamesPanelContext } from '../../context/JamesContext';
import { SidebarProvider, useSidebarContext } from '../../context/SidebarContext';
import { BreadcrumbProvider } from '../../context/BreadcrumbContext';
import { JamesPanel } from '../james/JamesPanel';

interface AppShellProps {
  children: React.ReactNode;
}

const AREA_TITLES: Record<string, string> = {
  sales: 'Sales', csm: 'CSM', gtm: 'GTM', marketing: 'Marketing', finance: 'Finance', organizer: 'Organizer',
  resources: 'Resources', documents: 'Brand docs', james: 'James', automations: 'Automations',
  integrations: 'Integrations', users: 'Users', settings: 'Settings', signatures: 'Signatures',
};

/** Words a URL segment does not spell the way the page names them. */
const SECTION_WORDS: Record<string, string> = {
  pnl: 'P&L', ae: 'AE', sdr: 'SDR', csm: 'CSM', gtm: 'GTM', seo: 'SEO', ai: 'AI', linkedin: 'LinkedIn', todos: 'To-dos',
};

/**
 * The browser tab's title from the route: "P&L · Finance · SEQUESTO OS",
 * "Home · SEQUESTO OS". Ids (a customer, a thread) are left out. The 404 page
 * sets its own after this runs.
 */
export function routeTitle(pathname: string): string {
  const [area, section] = pathname.split('/').filter(Boolean);
  if (!area) return 'Home · SEQUESTO OS';
  const areaTitle = AREA_TITLES[area];
  if (!areaTitle) return 'SEQUESTO OS';
  const sectionTitle =
    section && /^[a-z][a-z-]*$/.test(section)
      ? SECTION_WORDS[section] ?? section.split('-').map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ')
      : null;
  return [sectionTitle, areaTitle, 'SEQUESTO OS'].filter(Boolean).join(' · ');
}

function AppShellContent({ children }: AppShellProps) {
  const { isCollapsed } = useSidebarContext();
  const { pathname } = useLocation();
  // Layout effect, so a page's own useEffect (the 404's) can override it.
  useLayoutEffect(() => {
    document.title = routeTitle(pathname);
  }, [pathname]);
  const { togglePanel } = useJamesPanelContext();

  // ⌘K / Ctrl+K opens the James side panel from any page; the sidebar's
  // "Ask James" is the one visible entry point.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        togglePanel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePanel]);

  return (
    <div className="flex h-screen bg-surface-secondary">
      <div
        className={`h-full flex-none overflow-hidden transition-[width] duration-150 ease-out ${
          isCollapsed ? 'w-14' : 'w-64'
        }`}
      >
        <Sidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main className="relative flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  return (
    <SidebarProvider>
      <JamesProvider>
        <BreadcrumbProvider>
          <AppShellContent>{children}</AppShellContent>
        </BreadcrumbProvider>
        <JamesPanel />
      </JamesProvider>
    </SidebarProvider>
  );
}
